"use client";

import { useState } from "react";
import { Plus, Minus, Sparkles } from "lucide-react";
import Image from "next/image";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaWhatsapp,
  FaLinkedinIn,
} from "react-icons/fa";

export default function FAQPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      q: "How do I book an experience with Lily Lanka?",
      a: "Browse through our thoughtfully curated collection of experiences, select the activity that speaks to you, and tap Book Now. Our booking process is simple, intuitive, and designed to save you time.",
    },
    {
      q: "Can I modify or reschedule my booking?",
      a: "Yes. We understand that travel plans can change. You may manage your booking through the My Trips section in the app or contact our support team for personal assistance.",
    },
    {
      q: "What payment methods are available?",
      a: "We accept a range of secure and convenient payment options including debit cards, credit cards, mobile payments, and local bank transfers.",
    },
    {
      q: "How long do refunds take to process?",
      a: "Refunds are typically processed within five to seven working days. The exact timing may vary depending on your payment provider or financial institution.",
    },
    {
      q: "Will I receive confirmation after booking?",
      a: "Yes. Once your booking is completed, you will receive an immediate confirmation via email and an in app notification for your peace of mind.",
    },
    {
      q: "Is personal support available?",
      a: "Absolutely. Our experienced local team is available to guide you, answer questions, and provide assistance whenever you need it.",
    },
  ];

  const DIGITAL_OCEAN_SPACES_CDN =
    "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

  return (
    <div className="min-h-screen bg-[#faf8f5] dark:bg-[#0a0a0a] pt-16 md:pt-20">
      {/* FULL-WIDTH CREATIVE HEADER (same style as Support/FAQ) */}
      <header className="relative w-full overflow-hidden border-b border-gray-200 dark:border-gray-700">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage:
              "url('https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com/Site/images/background/support_background-3.jpg')",
            filter: "contrast(1.1) saturate(1.15)",
            transform: "scale(1.03)",
          }}
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 via-black/10 to-indigo-900/20" />

        {/* Decorative shapes */}
        <div className="pointer-events-none absolute -top-24 -left-28 h-80 w-80 rounded-full bg-purple-300/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -right-28 h-96 w-96 rounded-full bg-indigo-300/20 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="flex items-center justify-between gap-6">
            {/* Left: Title + underline */}
            <div className="flex items-start gap-4">
              <div>
                <div className="w-full flex items-center justify-center">
                  <h1 className="text-4xl md:text-4xl font-extrabold text-white tracking-tight drop-shadow-sm text-center">
                    Frequently Asked Questions
                  </h1>
                </div>
                <div className="mt-4 h-1 w-24 rounded-full bg-white/90" />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </header>

      {/* Main */}
      <div className="relative max-w-6xl mx-auto px-6 py-14">
        <div className="grid gap-10 lg:grid-cols-12 items-start">
          {/* Left column: Hero text + decorative image (glassy card like Support) */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="p-8 md:p-10">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                  Got questions before your journey begins? We've got your
                  answers packed and ready!
                </h2>

                <div className="mb-2">
                  <Image
                    src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/faq.jpeg`}
                    alt="Decorative Pattern"
                    width={800}
                    height={200}
                    className="rounded-2xl w-full border border-gray-200/60 dark:border-gray-700 shadow-sm"
                  />
                </div>
              </div>

              <div className="h-2 w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600" />
            </div>

            {/* FAQ Section Title (separate small glass box, same content) */}
            <div className="mt-8 rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="p-8 md:p-10">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Frequently asked questions
                </h3>
                <p className="text-base text-gray-600 dark:text-gray-300">
                  Travel with confidence knowing the details are taken care of.
                  Below are answers to the most common questions from our
                  guests. We've got your answers packed and ready!
                </p>
              </div>

              <div className="h-2 w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600" />
            </div>
          </div>

          {/* Right column: FAQ list (premium accordion style) */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="space-y-4">
                  {faqs.map((faq, idx) => (
                    <div
                      key={idx}
                      className="group rounded-2xl border border-gray-200/70 dark:border-gray-700 bg-white/60 dark:bg-[#27272a]/60 overflow-hidden transition hover:bg-white dark:hover:bg-[#27272a] hover:shadow-sm"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left"
                      >
                        <span className="font-semibold text-gray-900 dark:text-white text-base pr-4">
                          {faq.q}
                        </span>

                        <div className="relative flex-shrink-0">
                          <div className="absolute -inset-1 rounded-full bg-purple-600/15 blur-sm opacity-0 group-hover:opacity-100 transition" />
                          <div className="relative h-10 w-10 rounded-full bg-white dark:bg-[#3f3f46] flex items-center justify-center border border-gray-200/70 dark:border-gray-600 shadow-sm">
                            {openFaq === idx ? (
                              <Minus className="w-5 h-5 text-gray-700 dark:text-white" />
                            ) : (
                              <Plus className="w-5 h-5 text-gray-700 dark:text-white" />
                            )}
                          </div>
                        </div>
                      </button>

                      {openFaq === idx && (
                        <div className="px-6 pb-6 pt-0">
                          <div className="h-px w-full bg-gray-200/70 mb-4" />
                          <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
                            {faq.a}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="h-2 w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600" />
            </div>

            {/* Keep hidden (no content changes) */}
            <div className="mt-8 hidden">
              <div className="flex justify-center gap-3">
                <SocialIcon href="#">
                  <FaFacebookF className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaInstagram className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaTiktok className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaYoutube className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaWhatsapp className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaLinkedinIn className="text-white" />
                </SocialIcon>
              </div>
            </div>
          </div>
        </div>

        <div className="h-10" />
      </div>
    </div>
  );
}

/* Reusable components */
function SocialIcon({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-9 h-9 flex items-center justify-center bg-purple-800 rounded-full hover:bg-purple-700 transition text-base"
    >
      {children}
    </a>
  );
}
